import { pxToEm } from 'util/pxToEm';
import React, { ReactElement, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { Button } from '../Button';

export interface LeaderRequestPendingStateProps {
  requestedRole: string;
  onEdit: () => void;
  onCancel: () => Promise<void>;
}

export const LeaderRequestPendingState = ({
  requestedRole,
  onEdit,
  onCancel,
}: LeaderRequestPendingStateProps): ReactElement => {
  const [isCanceling, setIsCanceling] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);

  const handleCancel = async () => {
    if (isCanceling) return;

    setIsCanceling(true);
    try {
      await onCancel();
      setIsCanceled(true);
    } catch (error) {
      // Error is handled by parent component
    } finally {
      setIsCanceling(false);
    }
  };

  if (isCanceled) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          p: 16,
          borderRadius: 8,
          backgroundColor: '#F8F8F8',
        }}
      >
        <Typography
          sx={{
            fontSize: pxToEm(14),
            fontWeight: 500,
            color: '#666666',
            textAlign: 'center',
          }}
        >
          Request canceled
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {/* Request Card */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 12,
          backgroundColor: '#FFFFFF',
          border: '1px solid #E0E0E0',
        }}
      >
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography
            sx={{
              fontSize: pxToEm(16),
              fontWeight: 500,
              color: '#383838',
            }}
          >
            {requestedRole}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <AccessTime sx={{ fontSize: pxToEm(16), color: '#383838' }} />
            <Typography
              sx={{
                fontSize: pxToEm(13),
                fontWeight: 500,
                color: '#383838',
                lineHeight: 'normal',
              }}
            >
              Verification Pending
            </Typography>
          </Box>
        </Box>
        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            gap: 16,
            mt: 16,
          }}
        >
          <Typography
            onClick={handleCancel}
            sx={{
              fontSize: pxToEm(14),
              fontWeight: 400,
              color: '#23305F',
              cursor: isCanceling ? 'default' : 'pointer',
              opacity: isCanceling ? 0.5 : 1,
              '&:hover': {
                opacity: isCanceling ? 0.5 : 0.7,
              },
            }}
          >
            {isCanceling ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <CircularProgress size={12} sx={{ color: '#23305F' }} />
                Canceling...
              </Box>
            ) : (
              'Cancel'
            )}
          </Typography>
          <Button
            variant="new-paper"
            hoverEffect="none"
            onClick={onEdit}
            disabled={isCanceling}
            sx={{
              color: '#23305F',
              fontSize: pxToEm(14),
              fontWeight: 400,
              height: 47,
            }}
          >
            Edit
          </Button>
        </Box>
      </Box>

    </Box>
  );
};
